/**
 * AdminTopBar — Centered, function-grouped top navigation bar for admin/operator pages.
 *
 * Layout:
 *   [Logo]  [centered grouped Menu]  [UserMenu avatar]
 *
 * Groups:
 *   Approvals   — Listings · Interests · Claims · Invitations · Sales
 *   Arbitration — Disputes · Escalations
 *   Account & Security — Users · Audit Log · Billing
 *   System — Plant Analytics · Settings
 *
 * Last-opened submenu key is persisted in localStorage so the bar stays
 * "open" on the group the admin was last using.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Layout } from 'antd';
import type { MenuProps } from 'antd';
import {
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  SettingOutlined,
  FundOutlined,
  AuditOutlined,
  DollarOutlined,
  MessageOutlined,
  SwapOutlined,
  LinkOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import UserMenu from '../check/UserMenu';

const { Header } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const STORAGE_KEY = 'admin_topbar_open_keys';

function getStoredOpenKeys(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveOpenKeys(keys: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // ignore storage errors
  }
}

const MENU_ITEMS: MenuItem[] = [
  {
    key: 'approvals',
    label: 'Approvals',
    icon: <CheckCircleOutlined />,
    children: [
      { key: '/admin/change-requests', label: 'Change Requests', icon: <CheckCircleOutlined /> },
      { key: '/admin', label: 'Listing Review', icon: <AuditOutlined /> },
      { key: '/admin?tab=interests', label: 'Interests', icon: <MessageOutlined /> },
      { key: '/admin?tab=claims', label: 'Ownership Claims', icon: <SwapOutlined /> },
      { key: '/admin?tab=invitations', label: 'Claim Invitations', icon: <LinkOutlined /> },
      { key: '/admin?tab=sales', label: 'Sale Confirmations', icon: <ShoppingCartOutlined /> },
    ],
  },
  {
    key: 'arbitration',
    label: 'Arbitration',
    icon: <SafetyCertificateOutlined />,
    children: [
      { key: '/admin?tab=disputes', label: 'Disputes', icon: <MessageOutlined /> },
      { key: '/admin?tab=escalations', label: 'Escalations', icon: <AuditOutlined /> },
    ],
  },
  {
    key: 'account',
    label: 'Account & Security',
    icon: <TeamOutlined />,
    children: [
      { key: '/admin/users', label: 'Users', icon: <UserOutlined /> },
      { key: '/admin/billing', label: 'Billing & Plans', icon: <DollarOutlined /> },
      { key: '/admin?tab=audit', label: 'Audit Log', icon: <HistoryOutlined /> },
    ],
  },
  {
    key: 'system',
    label: 'System',
    icon: <SettingOutlined />,
    children: [
      { key: '/admin/plants', label: 'Plant Analytics', icon: <FundOutlined /> },
      { key: '/admin?tab=settings', label: 'Settings', icon: <SettingOutlined /> },
    ],
  },
];

function pathToSelectedKey(pathname: string, search: string): string {
  if (search) return `${pathname}${search}`;
  return pathname;
}

interface AdminTopBarProps {
  /** Height of the header in px; default 56 */
  height?: number;
}

const AdminTopBar: React.FC<AdminTopBarProps> = ({ height = 56 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>(getStoredOpenKeys);

  const selectedKey = pathToSelectedKey(location.pathname, location.search);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    saveOpenKeys(keys);
  };

  const handleSelect: MenuProps['onSelect'] = ({ key }) => {
    // key is the path (with optional query string)
    const [path, qs] = key.split('?');
    navigate(qs ? `${path}?${qs}` : path);
  };

  return (
    <Header
      data-testid="admin-top-bar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#141720',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '0 16px',
        height,
        lineHeight: `${height}px`,
      }}
    >
      {/* Left: logo / brand word-mark */}
      <div
        style={{ cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', minWidth: 100 }}
        onClick={() => navigate('/admin')}
        data-testid="admin-topbar-logo"
      >
        SolarRater
      </div>

      {/* Center: grouped navigation */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          onSelect={handleSelect}
          items={MENU_ITEMS}
          style={{
            background: 'transparent',
            borderBottom: 'none',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
          data-testid="admin-topbar-menu"
        />
      </div>

      {/* Right: user avatar / profile dropdown */}
      <div style={{ minWidth: 48, display: 'flex', justifyContent: 'flex-end' }}>
        <UserMenu />
      </div>
    </Header>
  );
};

export default AdminTopBar;
